import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../layouts/DashboardLayout';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL } from '../config';
import Cropper from 'react-easy-crop';
import RegistrationSurveyFields from '../components/RegistrationSurveyFields';
import {
    applyMealPreferencesToPlanningConfig,
    buildRegistrationIntakeSurveyPayload,
    normalizeRegistrationSurveyAnswers
} from '../utils/registrationSurveyModel';

const LUNCH_MODE_OPTIONS = [
    {
        value: 'light_practical_lunch',
        label: 'Hafif/pratik öğlen öner',
        description: 'Yan yemek havuzundan hafif ve hızlı öğlen önerileri üretirim.'
    },
    {
        value: 'extra_dinner_portion',
        label: 'Akşam yemeğini fazla porsiyon planla',
        description: 'Öğleni, akşamdan artacak ek porsiyonla çözerim.'
    },
    {
        value: 'separate_lunch_plan',
        label: 'Öğlen için ayrı yemek öner',
        description: 'Akşamdan bağımsız ayrı bir öğlen planı oluştururum.',
        badge: 'Yüksek Maliyet'
    },
    {
        value: 'leftover_only',
        label: 'Öğlen önerme, kalanlardan yerim',
        description: 'Ayrı menü açmam, yalnızca yumuşak bir hatırlatma bırakırım.'
    }
];

const LUNCH_SERVING_OPTIONS = [
    { value: 1, label: '1 kişi' },
    { value: 2, label: '2 kişi' },
    { value: 3, label: '3 kişi' },
    { value: 4, label: '4 kişi' },
    { value: 'family_size', label: 'Aile kişi sayısı kadar' }
];

const ALLERGEN_OPTIONS = [
    'Yok',
    'Laktoz',
    'Gluten',
    'Kuruyemiş',
    'Yumurta',
    'Balık',
    'Soya',
    'Susam',
    'Deniz Ürünleri'
];

const KitchenPlanner = ({ isDarkMode, toggleDarkMode }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showBanner, setShowBanner] = useState(true);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [showMemberModal, setShowMemberModal] = useState(false);
    const [editingMember, setEditingMember] = useState(null);
    const [dayState, setDayState] = useState(null);
    const [day7UnlockSeen, setDay7UnlockSeen] = useState(false);
    const [activePlannerTab, setActivePlannerTab] = useState('plan');
    const [weeklyKarne, setWeeklyKarne] = useState(null);
    const [karneLoading, setKarneLoading] = useState(false);
    const [showWeeklyNewBadge, setShowWeeklyNewBadge] = useState(false);

    // Cropper State
    const [imageToCrop, setImageToCrop] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [isCropping, setIsCropping] = useState(false);

    // Planning State
    const [config, setConfig] = useState({
        breakfast: { type: 'Kahvaltı Tabağı', servings: 2 },
        lunch: {
            structure: ['SOUP', 'MAIN'],
            preference: {
                mode: 'light_practical_lunch',
                selectedServingSize: 'family_size',
                extraServingSize: null,
                source: 'kitchen_planner',
                canChangeLater: true
            }
        },
        dinner: { structure: ['SOUP', 'MAIN', 'SALAD', 'DESSERT'] },
        notifications: {
            rescue_workday: '09:00',
            rescue_weekend: '11:00',
            auto_menu_workday: '17:00',
            auto_menu_weekend: '12:00',
            breakfast_workday: '08:30',
            breakfast_weekend: '10:00',
            send_weekend: true,
        },
        auto_share: false,
        hasSetAutoMenuTiming: false,
        skipsBreakfast: false,
        family: { partner: null, children: [] },
        user: { createdAt: new Date(), name: '', allergies: [], intakeSurvey: null }
    });
    const lunchServingMeaningText = (() => {
        const mode = String(config?.lunch?.preference?.mode || 'light_practical_lunch');
        if (mode === 'extra_dinner_portion') {
            return 'Bu sayı akşam yemeğine eklenecek öğlen porsiyonu anlamına gelir.';
        }
        if (mode === 'separate_lunch_plan') {
            return 'Bu sayı bağımsız öğlen planının kişi sayısı olarak kullanılacak.';
        }
        if (mode === 'leftover_only') {
            return 'Bu sayı stok düşümü yapmaz; yalnızca öğlen rutininin kişi ölçeğini anlamamı sağlar.';
        }
        return 'Bu sayı hafif/pratik öğlen önerilerinin porsiyon hedefi olarak kullanılacak.';
    })();

    useEffect(() => {
        localStorage.setItem('planner_success_badge_seen', '1');
    }, []);

    useEffect(() => {
        fetchConfig();
        fetchOnboardingDayState();
        fetchWeeklyKarne();

        // Listen for profile updates from other parts of the app
        const handleProfileUpdate = () => {
            console.log('ğŸ”„ Profile updated elsewhere, refreshing Kitchen Planner...');
            fetchConfig();
        };

        window.addEventListener('profileUpdated', handleProfileUpdate);
        return () => window.removeEventListener('profileUpdated', handleProfileUpdate);
    }, []);

    useEffect(() => {
        // Keep weekly report freshness while page is open so "weekly ready" notification is not missed.
        const timer = setInterval(() => {
            fetchWeeklyKarne();
        }, 60000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (!weeklyKarne) return;
        const versionKey = String(weeklyKarne.updated_at || weeklyKarne.generated_at || weeklyKarne.id || 'latest');
        const key = `karne_weekly_notified_${weeklyKarne.id || 'latest'}_${versionKey}`;
        if (sessionStorage.getItem(key)) return;
        sessionStorage.setItem(key, '1');
        setMessage({ text: 'Haftalık karnen hazır. Başarı grafiğini görüntüleyebilirsin.', type: 'success' });
        setTimeout(() => setMessage({ text: '', type: '' }), 3500);
    }, [weeklyKarne]);

    const getWeeklyKarneVersionKey = (report) => String(report?.updated_at || report?.generated_at || report?.id || 'latest');

    const markWeeklyKarneBadgeSeen = () => {
        if (!weeklyKarne) return;
        const versionKey = getWeeklyKarneVersionKey(weeklyKarne);
        const key = `karne_weekly_new_badge_seen_${weeklyKarne.id || 'latest'}_${versionKey}`;
        localStorage.setItem(key, '1');
        setShowWeeklyNewBadge(false);
    };

    useEffect(() => {
        if (!weeklyKarne) {
            setShowWeeklyNewBadge(false);
            return;
        }
        const versionKey = getWeeklyKarneVersionKey(weeklyKarne);
        const key = `karne_weekly_new_badge_seen_${weeklyKarne.id || 'latest'}_${versionKey}`;
        setShowWeeklyNewBadge(!localStorage.getItem(key));
    }, [weeklyKarne]);

    useEffect(() => {
        if (activePlannerTab === 'report') {
            markWeeklyKarneBadgeSeen();
        }
    }, [activePlannerTab, weeklyKarne]);

    useEffect(() => {
        const params = new URLSearchParams(location.search || '');
        const tab = String(params.get('tab') || '').toLowerCase();
        if (tab === 'report') {
            if (weeklyKarne) setActivePlannerTab('report');
            return;
        }
        if (tab === 'plan') {
            setActivePlannerTab('plan');
        }
    }, [location.search, weeklyKarne]);

    const fetchWeeklyKarne = async () => {
        try {
            setKarneLoading(true);
            const token = localStorage.getItem('authToken');
            const res = await axios.get(`${API_BASE_URL}/api/karne/weekly`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res?.data?.success && res?.data?.report) {
                setWeeklyKarne(res.data.report);
            } else {
                setWeeklyKarne(null);
            }
        } catch (_err) {
            setWeeklyKarne(null);
        } finally {
            setKarneLoading(false);
        }
    };

    const fetchOnboardingDayState = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch(`${API_BASE_URL}/api/profile/onboarding/day-state`, {
                credentials: 'include',
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (!res.ok) return;
            const data = await res.json();
            setDayState(data?.dayState || null);
        } catch (_error) {
            // no-op
        }
    };

    const fetchConfig = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            const res = await axios.get(`${API_BASE_URL}/api/planning/config`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                const fetchedConfig = res.data.config;

                // Inject or update partner in children array
                if (fetchedConfig.family) {
                    const children = [...(fetchedConfig.family.children || [])];
                    const partner = fetchedConfig.family.syncPartner || fetchedConfig.family.partner;
                    const partnerIndex = children.findIndex(c => c.role === 'partner');

                    if (partner) {
                        const partnerData = {
                            name: partner.name || 'Eşiniz',
                            role: 'partner',
                            id: partner.id,
                            photo: partner.photo || partner.avatar || partner.partnerAvatar || null,
                            birthDate: partner.birthDate || '',
                            allergies: partner.allergies || []
                        };

                        if (partnerIndex > -1) {
                            // Update existing partner in children array with fresh data from User/Profile tables
                            children[partnerIndex] = { ...children[partnerIndex], ...partnerData };
                        } else {
                            // Add if not present
                            children.unshift(partnerData);
                        }
                        fetchedConfig.family.children = children;
                    }
                }

                setConfig({
                    ...fetchedConfig,
                    lunch: {
                        ...(fetchedConfig.lunch || {}),
                        preference: {
                            mode: fetchedConfig?.lunch?.preference?.mode || 'light_practical_lunch',
                            selectedServingSize: fetchedConfig?.lunch?.preference?.selectedServingSize || 'family_size',
                            extraServingSize: fetchedConfig?.lunch?.preference?.extraServingSize || null,
                            source: fetchedConfig?.lunch?.preference?.source || 'kitchen_planner',
                            canChangeLater: fetchedConfig?.lunch?.preference?.canChangeLater !== false
                        }
                    }
                });
            }
        } catch (error) {
            console.error('? Error fetching planning config:', error);
            setMessage({ text: 'Ayarlar yüklenirken bir hata oluştu.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const token = localStorage.getItem('authToken');
            const surveyAnswers = normalizeRegistrationSurveyAnswers(config.user.intakeSurvey || {});
            const intakeSurvey = buildRegistrationIntakeSurveyPayload(surveyAnswers, {
                completedRegistration: config.user.intakeSurvey?.completedRegistration
            });
            const payload = applyMealPreferencesToPlanningConfig({
                ...config,
                user: {
                    ...config.user,
                    allergies: (surveyAnswers.q5 || []).filter((item) => item && item !== 'Yok'),
                    intakeSurvey
                }
            }, surveyAnswers.q8);

            console.log('ğŸ“¤ Sending planning config:', JSON.stringify(payload, null, 2));
            if (surveyAnswers?.q8?.lunch?.enabled !== false) {
                const lunchPreference = config?.lunch?.preference || {};
                const lunchMode = String(lunchPreference.mode || 'light_practical_lunch');
                const selectedServingSize = lunchPreference.selectedServingSize || 'family_size';
                payload.lunch = {
                    ...(payload.lunch || {}),
                    preference: {
                        mode: lunchMode,
                        selectedServingSize,
                        extraServingSize: lunchMode === 'extra_dinner_portion' ? selectedServingSize : null,
                        source: 'kitchen_planner',
                        canChangeLater: true,
                        lunchDecisionTrace: {
                            mode: lunchMode,
                            candidateSource: lunchMode === 'light_practical_lunch' ? 'service_type=yan_yemek' : null,
                            dinnerDuplicationPrevented: lunchMode !== 'extra_dinner_portion',
                            servingSize: selectedServingSize
                        }
                    }
                };
            }
            const res = await axios.post(`${API_BASE_URL}/api/planning/config`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                console.log('? Changes saved successfully:', res.data);
                setMessage({ text: 'Tüm değişiklikler başarıyla kaydedildi! ', type: 'success' });

                // Dispatch event to notify other components (like ProfilePage) that profile data has changed
                window.dispatchEvent(new Event('profileUpdated'));

                setTimeout(() => setMessage({ text: '', type: '' }), 4000);
            }
        } catch (error) {
            console.error('? Error saving planning config:', error);
            setMessage({ text: 'Ayarlar kaydedilirken bir hata oluştu.', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const toggleStructure = (period, type) => {
        const current = [...(config[period]?.structure || [])];
        const index = current.indexOf(type);
        if (index > -1) {
            current.splice(index, 1);
        } else {
            current.push(type);
        }
        setConfig({ ...config, [period]: { ...config[period], structure: current } });
    };

    const is7DayActive = () => {
        // Bypass for admins
        if (config.user.isAdmin) return true;
        const diff = (new Date() - new Date(config.user.createdAt)) / (1000 * 60 * 60 * 24);
        return diff >= 7;
    };

    const surveyAnswers = normalizeRegistrationSurveyAnswers(config.user.intakeSurvey || {});

    const getOneriZamaniValue = (q8) => {
        if (!q8) return 'Sadece Akşam';
        const b = q8.breakfast?.enabled !== false;
        const l = q8.lunch?.enabled !== false;
        const d = q8.dinner?.enabled !== false;

        if (!b && !l && d) return 'Sadece Akşam';
        if (!b && l && !d) return 'Sadece Öğle';
        if (!b && l && d) return 'Öğle ve Akşam';
        if (b && !l && d) return 'Sabah ve Akşam';
        if (b && l && d) return 'Günde 3 Öğün';
        return d ? 'Sadece Akşam' : 'Günde 3 Öğün';
    };

    const handleOneriZamaniChange = (val) => {
        let nextQ8 = { ...surveyAnswers.q8 };
        if (val === 'Sadece Akşam') {
            nextQ8.breakfast = { ...nextQ8.breakfast, enabled: false };
            nextQ8.lunch = { ...nextQ8.lunch, enabled: false };
            nextQ8.dinner = { ...nextQ8.dinner, enabled: true };
        } else if (val === 'Sadece Öğle') {
            nextQ8.breakfast = { ...nextQ8.breakfast, enabled: false };
            nextQ8.lunch = { ...nextQ8.lunch, enabled: true };
            nextQ8.dinner = { ...nextQ8.dinner, enabled: false };
        } else if (val === 'Öğle ve Akşam') {
            nextQ8.breakfast = { ...nextQ8.breakfast, enabled: false };
            nextQ8.lunch = { ...nextQ8.lunch, enabled: true };
            nextQ8.dinner = { ...nextQ8.dinner, enabled: true };
        } else if (val === 'Sabah ve Akşam') {
            nextQ8.breakfast = { ...nextQ8.breakfast, enabled: true };
            nextQ8.lunch = { ...nextQ8.lunch, enabled: false };
            nextQ8.dinner = { ...nextQ8.dinner, enabled: true };
        } else if (val === 'Günde 3 Öğün') {
            nextQ8.breakfast = { ...nextQ8.breakfast, enabled: true };
            nextQ8.lunch = { ...nextQ8.lunch, enabled: true };
            nextQ8.dinner = { ...nextQ8.dinner, enabled: true };
        }
        handlePlannerSurveyChange({
            ...surveyAnswers,
            q8: nextQ8
        });
    };

    const handleSurveyChange = (key, val) => {
        const newConfig = { ...config };
        if (!newConfig.user.intakeSurvey) newConfig.user.intakeSurvey = { answers: {} };
        if (!newConfig.user.intakeSurvey.answers) newConfig.user.intakeSurvey.answers = {};
        newConfig.user.intakeSurvey.answers[key] = val;
        setConfig(newConfig);
    };

    const handlePlannerSurveyChange = (nextAnswers) => {
        const intakeSurvey = buildRegistrationIntakeSurveyPayload(nextAnswers, {
            completedRegistration: config.user.intakeSurvey?.completedRegistration
        });
        const nextConfig = applyMealPreferencesToPlanningConfig({
            ...config,
            user: {
                ...config.user,
                allergies: (nextAnswers.q5 || []).filter((item) => item && item !== 'Yok'),
                intakeSurvey
            }
        }, nextAnswers.q8);
        setConfig(nextConfig);
    };

    const toggleAllergy = (allergy) => {
        let current = [...(config.user.allergies || [])];
        // Clean current of invalid stuff
        current = current.filter(a => a && a !== 'Yok' && a.toLowerCase() !== 'domates');

        const index = current.indexOf(allergy);
        if (index > -1) {
            current.splice(index, 1);
        } else if (allergy && allergy !== 'Yok' && allergy.toLowerCase() !== 'domates') {
            current.push(allergy);
        }
        setConfig({ ...config, user: { ...config.user, allergies: current } });
    };

    // Handler: Handle photo upload
    const handlePhotoUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            setMessage({ text: 'Fotoğraf boyutu 2MB\'dan küçük olmalıdır!', type: 'error' });
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            setImageToCrop(event.target.result);
            setIsCropping(true);
        };
        reader.readAsDataURL(file);
    };

    const onCropComplete = (croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    };

    const getCroppedImg = async (imageSrc, pixelCrop) => {
        const image = new Image();
        image.src = imageSrc;
        await new Promise((resolve) => (image.onload = resolve));

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;

        ctx.drawImage(
            image,
            pixelCrop.x,
            pixelCrop.y,
            pixelCrop.width,
            pixelCrop.height,
            0,
            0,
            pixelCrop.width,
            pixelCrop.height
        );

        return canvas.toDataURL('image/jpeg', 0.8);
    };

    const handleConfirmCrop = async () => {
        try {
            const croppedImage = await getCroppedImg(imageToCrop, croppedAreaPixels);
            setEditingMember(prev => ({ ...prev, photo: croppedImage }));
            setIsCropping(false);
            setImageToCrop(null);
        } catch (e) {
            console.error(e);
        }
    };

    // Helper: Calculate age from birthdate
    const calculateAge = (birthDate) => {
        if (!birthDate) return 0;
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        return age;
    };

    // Handler: Save family member
    const handleSaveMember = async () => {
        // Validation: Name required for non-self members
        if (!editingMember?.isSelf && !editingMember?.name?.trim()) {
            setMessage({ text: 'İsim zorunludur!', type: 'error' });
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);
            return;
        }

        // Validation: Role required for non-self members
        if (!editingMember?.isSelf && !editingMember?.role) {
            setMessage({ text: 'Rol seçimi zorunludur!', type: 'error' });
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);
            return;
        }

        try {
            setSaving(true);

            if (editingMember.isSelf) {
                // Update user's own allergies
                const updatedConfig = {
                    ...config,
                    user: {
                        ...config.user,
                        allergies: editingMember.allergies || []
                    }
                };
                setConfig(updatedConfig);

                const token = localStorage.getItem('authToken');
                await axios.post(`${API_BASE_URL}/api/planning/config`, {
                    user: { allergies: editingMember.allergies || [] }
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                // Add or update family member
                let updatedChildren = [...(config.family.children || [])];

                const memberData = {
                    name: editingMember.name,
                    role: editingMember.role,
                    photo: editingMember.photo || null, // Include photo
                    birthDate: editingMember.birthDate || '',
                    allergies: editingMember.allergies || []
                };

                if (editingMember.index !== undefined && editingMember.index !== null) {
                    // Update existing member - validate index
                    if (editingMember.index >= 0 && editingMember.index < updatedChildren.length) {
                        updatedChildren[editingMember.index] = memberData;
                    } else {
                        throw new Error(`Invalid index: ${editingMember.index}`);
                    }
                } else {
                    // Add new member
                    updatedChildren.push(memberData);
                }

                // Prepare planning payload
                const planningPayload = {
                    family: { children: updatedChildren }
                };

                // If saving a partner, also sync the photo to intakeSurvey.partnerAvatar if it exists
                // Note: The backend POST /api/profile handles the actual User.avatar updates for partner usually,
                // but here we are in Kitchen Planner (planning.js).
                // Let's ensure the local state is updated.

                const updatedConfig = {
                    ...config,
                    family: {
                        ...config.family,
                        children: updatedChildren
                    }
                };
                setConfig(updatedConfig);

                const token = localStorage.getItem('authToken');
                const saveRes = await axios.post(`${API_BASE_URL}/api/planning/config`, planningPayload, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                // If specialized partner sync is needed, we could trigger another profile update here,
                // but planning.js + profileUpdated event usually suffices if the backend handles it.
                if (memberData.role === 'partner' && memberData.photo) {
                    // Optional: sync with profile API for immediate User table update if planning.js doesn't do it
                    await axios.post(`${API_BASE_URL}/api/profile`, {
                        intakeSurvey: { partnerAvatar: memberData.photo }
                    }, {
                        headers: { Authorization: `Bearer ${token}` }
                    }).catch(e => console.warn('Partner avatar sync failed', e));
                }
            }

            // Dispatch event for other components
            window.dispatchEvent(new Event('profileUpdated'));

            setShowMemberModal(false);
            setEditingMember(null);

            // Success message
            setMessage({
                text: editingMember.isSelf
                    ? 'Bilgileriniz güncellendi!'
                    : editingMember.index !== undefined
                        ? 'Aile bireyi güncellendi!'
                        : 'Yeni aile bireyi eklendi!',
                type: 'success'
            });
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);

        } catch (error) {
            console.error('Kaydetme hatası:', error);
            const errorMsg = error.response?.data?.error || error.message || 'Bir hata oluştu!';
            setMessage({ text: errorMsg, type: 'error' });
            setTimeout(() => setMessage({ text: '', type: '' }), 5000);
        } finally {
            setSaving(false);
        }
    };

    // Handler: Delete family member
    const handleDeleteMember = async (index) => {
        if (!config.family.children || !config.family.children[index]) {
            setMessage({ text: 'Aile bireyi bulunamadı!', type: 'error' });
            return;
        }

        const member = config.family.children[index];

        if (!confirm(`${member.name} isimli aile bireyini silmek istediğinize emin misiniz?`)) {
            return;
        }

        try {
            setSaving(true);

            const updatedChildren = config.family.children.filter((_, idx) => idx !== index);

            const updatedConfig = {
                ...config,
                family: {
                    ...config.family,
                    children: updatedChildren
                }
            };
            setConfig(updatedConfig);

            const token = localStorage.getItem('authToken');
            await axios.post(`${API_BASE_URL}/api/planning/config`, {
                family: { children: updatedChildren }
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            window.dispatchEvent(new Event('profileUpdated'));

            setMessage({ text: 'Aile bireyi silindi.', type: 'success' });
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);

        } catch (error) {
            console.error('Silme hatası:', error);
            setMessage({ text: 'Bir hata oluştu!', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const hasPlannerPrefillData = Boolean(
        config?.user?.name ||
        config?.user?.intakeSurvey?.answers?.q1 ||
        config?.user?.intakeSurvey?.answers?.q2 ||
        config?.family?.children?.length
    );

    const isDay7Unlock = Number(dayState?.onboardingDay || 0) >= 7;
    const isDay7UnlockCompleted = Array.isArray(dayState?.completedSteps) && dayState.completedSteps.includes('day7_planner_unlock_seen');

    useEffect(() => {
        const markDay7UnlockSeen = async () => {
            if (!isDay7Unlock || isDay7UnlockCompleted || day7UnlockSeen) return;
            const token = localStorage.getItem('authToken');
            if (!token) return;

            const currentSteps = Array.isArray(dayState?.completedSteps) ? dayState.completedSteps : [];
            const mergedSteps = Array.from(new Set([...currentSteps, 'day7_planner_unlock_seen']));

            try {
                await fetch(`${API_BASE_URL}/api/profile/onboarding/day-state`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        completedSteps: mergedSteps,
                        lastShownAt: {
                            ...(dayState?.lastShownAt || {}),
                            day7_planner_unlock: new Date().toISOString()
                        }
                    })
                });

                await fetch(`${API_BASE_URL}/api/profile/onboarding/event`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        event: 'onboarding_day7_planner_unlock_seen',
                        surface: 'planner_unlock',
                        mode: 'inline',
                        reason: 'day7_planner_unlock'
                    })
                });
                setDay7UnlockSeen(true);
            } catch (_error) {
                // no-op
            }
        };

        markDay7UnlockSeen();
    }, [isDay7Unlock, isDay7UnlockCompleted, day7UnlockSeen, dayState]);

    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center bg-white dark:bg-[#1a1a1a]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
            </div>
        );
    }

    return (
        <DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}>
            <div className="max-w-5xl mx-auto px-4 py-12 md:px-8 pb-20">
                <header className="mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div>
                        <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-2 font-display">Planlayın</h1>
                        <p className="text-slate-500 dark:text-slate-400">Mutfak tercihlerini ve günlük yemek alışkanlıklarını yönetin.</p>
                    </div>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className={`${saving ? 'opacity-70 cursor-not-allowed' : 'hover:bg-red-700'} bg-primary text-white px-6 py-2.5 rounded-full font-semibold transition-all shadow-lg shadow-red-500/20 flex items-center gap-2 w-fit`}
                    >
                        <span className="material-symbols-outlined text-[20px]">{saving ? 'sync' : 'save'}</span>
                        {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                    </button>
                </header>


                {/* Success/Error Message */}
                <AnimatePresence>
                    {message.text && (
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${message.type === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200 border border-green-200 dark:border-green-800' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200 border border-red-200 dark:border-red-800'}`}
                        >
                            <span className="material-symbols-outlined">{message.type === 'success' ? 'check_circle' : 'error'}</span>
                            <p className="font-medium">{message.text}</p>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="mb-6 flex items-center gap-2 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-2">
                    <button
                        onClick={() => setActivePlannerTab('plan')}
                        className={`px-4 py-2 rounded-xl text-sm font-bold transition ${activePlannerTab === 'plan'
                            ? 'bg-primary text-white shadow'
                            : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                            }`}
                    >
                        Plan Ayarları
                    </button>
                    <button
                        onClick={() => {
                            if (!weeklyKarne) return;
                            setActivePlannerTab('report');
                            markWeeklyKarneBadgeSeen();
                        }}
                        disabled={!weeklyKarne}
                        className={`px-4 py-2 rounded-xl text-sm font-bold transition ${activePlannerTab === 'report'
                            ? 'bg-primary text-white shadow'
                            : 'text-slate-600 dark:text-slate-300'
                            } ${weeklyKarne ? 'hover:bg-slate-100 dark:hover:bg-slate-700' : 'opacity-50 cursor-not-allowed'}`}
                    >
                        Haftalık Başarı Grafiği
                    </button>
                </div>

                {activePlannerTab === 'report' && weeklyKarne && (
                    <div className="mb-8 rounded-2xl border border-red-200 dark:border-red-900/40 bg-white dark:bg-slate-800 p-5">
                        <div className="inline-flex items-center gap-2 rounded-full bg-red-500 text-white px-3 py-1 text-xs font-bold mb-4">
                            Başarı Grafiği
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Bu Hafta Ne Oldu</h3>
                        <ul className="space-y-2 text-slate-700 dark:text-slate-200">
                            {(weeklyKarne?.payload_json?.summaryBullets || []).map((item, idx) => (
                                <li key={idx}>• {item}</li>
                            ))}
                        </ul>
                        <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
                            {weeklyKarne?.payload_json?.weeklyMoodText || 'Haftalık karne hazır.'}
                        </p>
                    </div>
                )}

                {activePlannerTab === 'plan' && (
                    <>
                        {!hasPlannerPrefillData && (
                            <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900/40 dark:bg-amber-950/20">
                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                    Şimdilik sistem otomatik yönetiyor.
                                </p>
                                <p className="text-xs text-slate-600 dark:text-slate-300">
                                    Veriler geldikçe Planlayın alanı otomatik doldurulacak; istersen bu ekrandan şimdi de düzenleyebilirsin.
                                </p>
                            </div>
                        )}

                        {isDay7Unlock && (
                            <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 dark:border-emerald-900/40 dark:bg-emerald-950/20">
                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                    Artık mutfağını tamamen sen yönetebilirsin.
                                </p>
                                <p className="text-xs text-slate-600 dark:text-slate-300">
                                    Planlayın alanı mevcut verilerle hazırlandı. İstersen buradan hemen düzenleyebilirsin.
                                </p>
                            </div>
                        )}
                    </>
                )}

                {/* 7 Day Banner */}
                {showBanner && is7DayActive() && (
                    <div className="mb-10 relative overflow-hidden bg-gradient-to-r from-red-50 to-white dark:from-red-900/10 dark:to-slate-800 p-4 md:p-5 rounded-2xl border border-red-100 dark:border-red-900/30 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                                <span className="material-symbols-outlined text-primary">verified</span>
                            </div>
                            <div>
                                <p className="text-slate-800 dark:text-slate-200 font-medium leading-tight">
                                    Bu alan, 7 günlük kullanım sonrası açıldı.
                                    <span className="block md:inline text-slate-500 dark:text-slate-400 font-normal ml-1">Artık mutfağının kurallarını sen belirliyorsun.</span>
                                </p>
                            </div>
                        </div>
                        <button onClick={() => setShowBanner(false)} aria-label="Kapat" className="text-slate-400 hover:text-primary transition-colors p-1">
                            <span className="material-symbols-outlined text-[20px]">close</span>
                        </button>
                        <div className="absolute -right-4 -bottom-4 opacity-5 pointer-events-none">
                            <span className="material-symbols-outlined text-[80px]">celebration</span>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Sidebar Column */}
                    <div className="lg:col-span-1 space-y-8">
                        <section className="relative overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900 dark:from-slate-900 dark:to-black p-6 rounded-2xl shadow-lg text-white">
                            {showWeeklyNewBadge && (
                                <div className="absolute top-4 right-4 z-20">
                                    <span className="bg-primary text-white text-[10px] font-bold px-2 py-0.5 rounded-full ring-2 ring-slate-800">YENİ</span>
                                </div>
                            )}
                            <div className="relative z-10">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="material-symbols-outlined text-yellow-400">trending_up</span>
                                    <h2 className="text-xl font-bold font-display">Haftalık Başarı Grafiği</h2>
                                </div>
                                <p className="text-sm text-slate-300">
                                    {karneLoading ? 'Karne kontrol ediliyor...' : weeklyKarne ? 'Bu hafta raporun hazır.' : 'Rapor hazır olduğunda burada görünecek.'}
                                </p>
                                {weeklyKarne && (
                                    <button
                                        onClick={() => {
                                            setActivePlannerTab('report');
                                            markWeeklyKarneBadgeSeen();
                                        }}
                                        className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold hover:bg-red-700 transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">insights</span>
                                        Grafiği Aç
                                    </button>
                                )}
                            </div>
                            <div className="absolute -right-8 -bottom-8 opacity-10">
                                <span className="material-symbols-outlined text-[120px]">analytics</span>
                            </div>
                        </section>

                        {/* Family Bireyleri */}
                        <section className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-2">
                                    <span className="material-symbols-outlined text-primary">groups</span>
                                    <h2 className="text-xl font-bold font-display">Aile Bireyleri</h2>
                                </div>
                                {is7DayActive() && (
                                    <button
                                        onClick={() => {
                                            setEditingMember({ name: '', role: 'child', birthDate: '', allergies: [] });
                                            setShowMemberModal(true);
                                        }}
                                        className="text-primary hover:bg-red-50 dark:hover:bg-red-900/20 p-1 rounded-full transition-colors"
                                    >
                                        <span className="material-symbols-outlined">add_circle</span>
                                    </button>
                                )}
                            </div>

                            {!is7DayActive() ? (
                                /* 7 Gün Öncesi - Locked State */
                                <div className="text-center py-8">
                                    <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <span className="material-symbols-outlined text-4xl text-slate-400">lock</span>
                                    </div>
                                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                                        Aile Yönetimi Kilitli
                                    </p>
                                    <p className="text-xs text-slate-500 max-w-xs mx-auto">
                                        Bu özellik hesabınız oluşturulduktan 7 gün sonra aktif olacak.
                                        Şu an sadece görüntüleme modundasınız.
                                    </p>
                                    <div className="mt-4 space-y-3">
                                        {/* User (Self) - Read Only */}
                                        <div className="flex items-center gap-3 p-3 bg-slate-50/50 dark:bg-slate-700/30 rounded-xl border border-slate-100 dark:border-slate-700">
                                            <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center border-2 border-primary/20">
                                                <span className="material-symbols-outlined text-primary">person</span>
                                            </div>
                                            <div className="flex-1">
                                                <p className="font-medium text-sm">{config.user.name}</p>
                                                <p className="text-xs text-slate-500">Yönetici</p>
                                            </div>
                                        </div>

                                        {/* Baby - Read Only */}
                                        {config.family.hasBaby && config.family.babyName && (
                                            <div className="flex items-center gap-3 p-3 bg-pink-50/50 dark:bg-pink-900/10 rounded-xl border border-pink-100 dark:border-pink-900/30">
                                                <div className="w-10 h-10 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center border-2 border-pink-500/20">
                                                    <span className="material-symbols-outlined text-pink-500">baby_changing_station</span>
                                                </div>
                                                <div className="flex-1">
                                                    <p className="font-medium text-sm">{config.family.babyName}</p>
                                                    <p className="text-xs text-slate-500">Bebek</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                /* 7 Gün Sonrası - Full CRUD */
                                <div className="space-y-4">
                                    {/* User (Self) - Editable */}
                                    <div className="p-4 bg-slate-50/50 dark:bg-slate-700/30 rounded-xl border border-slate-100 dark:border-slate-700">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-3">
                                                {config.user.avatar ? (
                                                    <img src={config.user.avatar} alt={config.user.name} className="w-10 h-10 rounded-full object-cover border-2 border-primary/20" />
                                                ) : (
                                                    <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center border-2 border-primary/20">
                                                        <span className="material-symbols-outlined text-primary">person</span>
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="font-medium">{config.user.name}</p>
                                                    <p className="text-xs text-slate-500">Yönetici</p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    setEditingMember({
                                                        name: config.user.name,
                                                        role: 'self',
                                                        allergies: config.user.allergies || [],
                                                        isSelf: true
                                                    });
                                                    setShowMemberModal(true);
                                                }}
                                                className="text-primary hover:bg-white dark:hover:bg-slate-800 p-2 rounded-lg transition-colors"
                                            >
                                                <span className="material-symbols-outlined text-[20px]">edit</span>
                                            </button>
                                        </div>

                                        {/* Allergen Chips */}
                                        {config.user.allergies && config.user.allergies.length > 0 && (
                                            <div className="flex flex-wrap gap-1 pl-[52px]">
                                                {config.user.allergies.filter(a => a !== 'Yok').map(a => (
                                                    <span key={a} className="text-[10px] px-2 py-0.5 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-full">
                                                        {a}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Other Family Members */}
                                    {config.family.children && config.family.children.map((member, idx) => (
                                        <div key={idx} className="p-4 bg-slate-50/50 dark:bg-slate-700/30 rounded-xl border border-slate-100 dark:border-slate-700">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-3">
                                                    {member.photo ? (
                                                        <img src={member.photo} alt={member.name} className="w-10 h-10 rounded-full object-cover border-2 border-primary/20" />
                                                    ) : (
                                                        <div className={`w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center border-2 ${member.role === 'partner' ? 'border-indigo-500/20' :
                                                            member.role === 'baby' ? 'border-pink-500/20' :
                                                                'border-orange-500/20'
                                                            }`}>
                                                            <span className={`material-symbols-outlined ${member.role === 'partner' ? 'text-indigo-500' :
                                                                member.role === 'baby' ? 'text-pink-500' :
                                                                    'text-orange-500'
                                                                }`}>
                                                                {member.role === 'partner' ? 'handshake' :
                                                                    member.role === 'baby' ? 'baby_changing_station' :
                                                                        'child_care'}
                                                            </span>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <p className="font-medium">{member.name}</p>
                                                        <p className="text-xs text-slate-500">
                                                            {member.role === 'partner' ? 'Partner/Eş' :
                                                                member.role === 'child' ? 'Çocuk' :
                                                                    member.role === 'baby' ? 'Bebek' :
                                                                        'Diğer'}
                                                            {member.birthDate && ` (${calculateAge(member.birthDate)} yaş)`}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        onClick={() => {
                                                            setEditingMember({ ...member, index: idx });
                                                            setShowMemberModal(true);
                                                        }}
                                                        className="text-primary hover:bg-white dark:hover:bg-slate-800 p-2 rounded-lg transition-colors"
                                                    >
                                                        <span className="material-symbols-outlined text-[20px]">edit</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteMember(idx)}
                                                        className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 p-2 rounded-lg transition-colors"
                                                    >
                                                        <span className="material-symbols-outlined text-[20px]">delete</span>
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Allergen Chips */}
                                            {member.allergies && member.allergies.length > 0 && (
                                                <div className="flex flex-wrap gap-1 pl-[52px]">
                                                    {member.allergies.map(a => (
                                                        <span key={a} className="text-[10px] px-2 py-0.5 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-full">
                                                            {a}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    {/* Empty State */}
                                    {(!config.family.children || config.family.children.length === 0) && (
                                        <div className="text-center py-8 text-slate-400">
                                            <span className="material-symbols-outlined text-4xl mb-2">group_add</span>
                                            <p className="text-sm">Henüz aile bireyi eklenmedi.</p>
                                            <p className="text-xs mt-1">Yukarıdaki + butonuna tıklayarak ekleyebilirsiniz.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>

                        {/* Anket Özeti */}
                        <section className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
                            <div className="flex items-center gap-2 mb-6">
                                <span className="material-symbols-outlined text-primary">assignment</span>
                                <h2 className="text-xl font-bold font-display">Anket Özeti</h2>
                            </div>
                            <div className="space-y-5">
                                {/* GÜNLÜK RUTİN */}
                                <div>
                                    <label className="text-[10px] sm:text-xs font-bold text-slate-400 dark:text-slate-400 tracking-wider mb-1.5 uppercase block">
                                        Günlük Rutin
                                    </label>
                                    <div className="relative flex items-center">
                                        <select
                                            value={surveyAnswers.q1}
                                            onChange={(e) => handlePlannerSurveyChange({ ...surveyAnswers, q1: e.target.value })}
                                            className="w-full bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700/60 text-slate-800 dark:text-white rounded-xl py-2.5 pl-3.5 pr-10 text-sm font-medium focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 appearance-none cursor-pointer transition-all"
                                        >
                                            <option value="" disabled className="text-slate-500 bg-white dark:bg-slate-800">Seçiniz...</option>
                                            <option value="Yoğun Çalışan" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">Yoğun Çalışan</option>
                                            <option value="Ev Hanımı/Erkeği" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">Ev Hanımı/Erkeği</option>
                                            <option value="Öğrenci" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">Öğrenci</option>
                                            <option value="Emekli" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">Emekli</option>
                                        </select>
                                        <span className="material-symbols-outlined absolute right-3 text-slate-400 dark:text-slate-500 pointer-events-none text-[20px]">
                                            expand_more
                                        </span>
                                    </div>
                                </div>

                                {/* MUTFAK BECERİSİ */}
                                <div>
                                    <label className="text-[10px] sm:text-xs font-bold text-slate-400 dark:text-slate-400 tracking-wider mb-1.5 uppercase block">
                                        Mutfak Becerisi
                                    </label>
                                    <div className="relative flex items-center">
                                        <select
                                            value={surveyAnswers.q2}
                                            onChange={(e) => handlePlannerSurveyChange({ ...surveyAnswers, q2: e.target.value })}
                                            className="w-full bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700/60 text-slate-800 dark:text-white rounded-xl py-2.5 pl-3.5 pr-10 text-sm font-medium focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 appearance-none cursor-pointer transition-all"
                                        >
                                            <option value="" disabled className="text-slate-500 bg-white dark:bg-slate-800">Seçiniz...</option>
                                            <option value="Acemi Şef" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">Acemi Şef</option>
                                            <option value="Orta Seviye" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">Orta Seviye</option>
                                            <option value="Mutfak Ustası" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">Mutfak Ustası</option>
                                        </select>
                                        <span className="material-symbols-outlined absolute right-3 text-slate-400 dark:text-slate-500 pointer-events-none text-[20px]">
                                            expand_more
                                        </span>
                                    </div>
                                </div>

                                {/* BESLENME TARZI */}
                                <div>
                                    <label className="text-[10px] sm:text-xs font-bold text-slate-400 dark:text-slate-400 tracking-wider mb-1.5 uppercase block">
                                        Beslenme Tarzı
                                    </label>
                                    <div className="relative flex items-center">
                                        <select
                                            value={surveyAnswers.q4}
                                            onChange={(e) => handlePlannerSurveyChange({ ...surveyAnswers, q4: e.target.value })}
                                            className="w-full bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700/60 text-slate-800 dark:text-white rounded-xl py-2.5 pl-3.5 pr-10 text-sm font-medium focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 appearance-none cursor-pointer transition-all"
                                        >
                                            <option value="" disabled className="text-slate-500 bg-white dark:bg-slate-800">Seçiniz...</option>
                                            <option value="Etçil" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">Etçil</option>
                                            <option value="Vejetaryen" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">Vejetaryen</option>
                                            <option value="Pesketaryen" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">Pesketaryen</option>
                                            <option value="Vegan" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">Vegan</option>
                                        </select>
                                        <span className="material-symbols-outlined absolute right-3 text-slate-400 dark:text-slate-500 pointer-events-none text-[20px]">
                                            expand_more
                                        </span>
                                    </div>
                                </div>

                                {/* PİŞİRME SIKLIĞI */}
                                <div>
                                    <label className="text-[10px] sm:text-xs font-bold text-slate-400 dark:text-slate-400 tracking-wider mb-1.5 uppercase block">
                                        Pişirme Sıklığı
                                    </label>
                                    <div className="relative flex items-center">
                                        <select
                                            value={surveyAnswers.q6 || ""}
                                            onChange={(e) => handlePlannerSurveyChange({ ...surveyAnswers, q6: e.target.value })}
                                            className="w-full bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700/60 text-slate-800 dark:text-white rounded-xl py-2.5 pl-3.5 pr-10 text-sm font-medium focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 appearance-none cursor-pointer transition-all"
                                        >
                                            <option value="" disabled className="text-slate-500 bg-white dark:bg-slate-800">Seçiniz...</option>
                                            <option value="1-2 Gün" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">1-2 Gün</option>
                                            <option value="3-4 Gün" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">3-4 Gün</option>
                                            <option value="Her Gün" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">Her Gün</option>
                                        </select>
                                        <span className="material-symbols-outlined absolute right-3 text-slate-400 dark:text-slate-500 pointer-events-none text-[20px]">
                                            expand_more
                                        </span>
                                    </div>
                                </div>

                                {/* HARCAMA TAKİBİ */}
                                <div>
                                    <label className="text-[10px] sm:text-xs font-bold text-slate-400 dark:text-slate-400 tracking-wider mb-1.5 uppercase block">
                                        Harcama Takibi
                                    </label>
                                    <div className="relative flex items-center">
                                        <select
                                            value={surveyAnswers.q7}
                                            onChange={(e) => handlePlannerSurveyChange({ ...surveyAnswers, q7: e.target.value })}
                                            className="w-full bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700/60 text-slate-800 dark:text-white rounded-xl py-2.5 pl-3.5 pr-10 text-sm font-medium focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 appearance-none cursor-pointer transition-all"
                                        >
                                            <option value="" disabled className="text-slate-500 bg-white dark:bg-slate-800">Seçiniz...</option>
                                            <option value="Evet" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">Evet</option>
                                            <option value="Hayır ama istiyorum" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">Hayır ama istiyorum</option>
                                            <option value="Hayır, gerek yok" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">Hayır, gerek yok</option>
                                        </select>
                                        <span className="material-symbols-outlined absolute right-3 text-slate-400 dark:text-slate-500 pointer-events-none text-[20px]">
                                            expand_more
                                        </span>
                                    </div>
                                </div>

                                {/* ÖNERİ ZAMANI */}
                                <div>
                                    <label className="text-[10px] sm:text-xs font-bold text-slate-400 dark:text-slate-400 tracking-wider mb-1.5 uppercase block">
                                        Öneri Zamanı
                                    </label>
                                    <div className="relative flex items-center">
                                        <select
                                            value={getOneriZamaniValue(surveyAnswers.q8)}
                                            onChange={(e) => handleOneriZamaniChange(e.target.value)}
                                            className="w-full bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700/60 text-slate-800 dark:text-white rounded-xl py-2.5 pl-3.5 pr-10 text-sm font-medium focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 appearance-none cursor-pointer transition-all"
                                        >
                                            <option value="Sadece Akşam" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">Sadece Akşam</option>
                                            <option value="Sadece Öğle" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">Sadece Öğle</option>
                                            <option value="Öğle ve Akşam" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">Öğle ve Akşam</option>
                                            <option value="Sabah ve Akşam" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">Sabah ve Akşam</option>
                                            <option value="Günde 3 Öğün" className="text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800">Günde 3 Öğün</option>
                                        </select>
                                        <span className="material-symbols-outlined absolute right-3 text-slate-400 dark:text-slate-500 pointer-events-none text-[20px]">
                                            expand_more
                                        </span>
                                    </div>
                                </div>

                                {/* ALERJİLER */}
                                <div className="pt-2">
                                    <label className="text-[10px] sm:text-xs font-bold text-slate-400 dark:text-slate-400 tracking-wider mb-2 uppercase block">
                                        Alerjiler
                                    </label>
                                    <div className="flex flex-wrap gap-2 mb-2">
                                        {config.user.allergies.filter(a => a !== 'Yok').length > 0 ? (
                                            config.user.allergies.filter(a => a !== 'Yok').map(allergy => (
                                                <span key={allergy} className="px-2.5 py-1 bg-blue-500/10 text-blue-500 dark:text-blue-400 border border-blue-500/25 text-[11px] font-bold rounded-md transition-all">
                                                    {allergy}
                                                </span>
                                            ))
                                        ) : (
                                            <p className="text-xs text-slate-400 dark:text-slate-500 italic">Hassasiyet bulunamadı</p>
                                        )}
                                    </div>
                                    <p className="text-[10px] text-slate-400 dark:text-slate-500 italic flex items-center gap-1.5 mt-2">
                                        <span className="material-symbols-outlined text-[14px]">info</span>
                                        Alerjileri "Aile Bireyleri" bölümünden düzenleyebilirsiniz.
                                    </p>
                                </div>
                            </div>
                        </section>

                    </div>

                    {/* Main Settings Column */}
                    <div className="lg:col-span-2 space-y-8">
                        <section className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
                            <div className="flex items-center gap-2 mb-8">
                                <span className="material-symbols-outlined text-primary">restaurant_menu</span>
                                <h2 className="text-2xl font-bold font-display">Günlük Yemek Alışkanlıkları</h2>
                            </div>
                            <div className="space-y-10">
                                {/* Global Servings Setup */}
                                <div className="bg-slate-50 dark:bg-slate-900/50 p-6 rounded-2xl border border-slate-100 dark:border-slate-700 mb-8">
                                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                                        <div className="flex items-center gap-4 text-center md:text-left">
                                            <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                                                <span className="material-symbols-outlined text-primary text-3xl">groups</span>
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold">Kaç Kişilik Yemek Pişiriyorsunuz?</h3>
                                                <p className="text-sm text-slate-500">Mutfak porsiyonlarınızı buradan belirleyebilirsiniz.</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center bg-white dark:bg-slate-800 rounded-2xl shadow-sm p-2 border border-slate-200 dark:border-slate-700 min-w-[160px]">
                                            <button onClick={() => setConfig({ ...config, breakfast: { ...config.breakfast, servings: Math.max(1, config.breakfast.servings - 1) } })} className="w-12 h-12 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors">
                                                <span className="material-symbols-outlined">remove</span>
                                            </button>
                                            <input className="w-full text-center bg-transparent border-none text-xl font-bold p-0 focus:ring-0 dark:text-white" type="text" value={config.breakfast.servings} readOnly />
                                            <button onClick={() => setConfig({ ...config, breakfast: { ...config.breakfast, servings: config.breakfast.servings + 1 } })} className="w-12 h-12 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors">
                                                <span className="material-symbols-outlined">add</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Kahvaltı Tercihi Toggle */}
                                <div className="flex items-center justify-between p-4 bg-orange-50/50 dark:bg-orange-900/10 rounded-xl border border-orange-100 dark:border-orange-900/20">
                                    <div className="flex items-center gap-3">
                                        <span className="material-symbols-outlined text-orange-500">coffee</span>
                                        <div>
                                            <p className="font-bold text-slate-800 dark:text-slate-200">Kahvaltı Yapıyor musunuz?</p>
                                            <p className="text-xs text-slate-500">Yapmıyorsanız kahvaltı bildirimleri ve önerileri gizlenir.</p>
                                        </div>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={!config.skipsBreakfast}
                                            onChange={() => setConfig({ ...config, skipsBreakfast: !config.skipsBreakfast })}
                                        />
                                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary"></div>
                                    </label>
                                </div>

                                {/* Sabah Section */}
                                {(!config.skipsBreakfast) && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="space-y-4"
                                    >
                                        <div className="flex items-center gap-3 pb-2 border-b border-slate-100 dark:border-slate-700">
                                            <span className="material-symbols-outlined text-orange-500">light_mode</span>
                                            <h3 className="text-lg font-semibold uppercase tracking-wide">Sabah</h3>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="grid grid-cols-2 gap-3 items-center">
                                                <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Ana Seçim</label>
                                                <select
                                                    value={config.breakfast.type}
                                                    onChange={(e) => setConfig({ ...config, breakfast: { ...config.breakfast, type: e.target.value } })}
                                                    className="bg-slate-50 dark:bg-slate-900 border-none rounded-lg text-sm focus:ring-primary dark:text-white p-2.5"
                                                >
                                                    <option>Kahvaltı Tabağı</option>
                                                    <option>Yulaf Lapas?</option>
                                                    <option>Omlet</option>
                                                    <option>Pankek / Krep</option>
                                                    <option>Simit / Poğaça</option>
                                                </select>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}

                                {/* Öğle & Akşam */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                                    <div className="space-y-6">
                                        <div className="flex items-center gap-3 pb-2 border-b border-slate-100 dark:border-slate-700">
                                            <span className="material-symbols-outlined text-yellow-500">wb_sunny</span>
                                            <h3 className="text-lg font-semibold uppercase tracking-wide">Öğle</h3>
                                        </div>
                                        {surveyAnswers?.q8?.lunch?.enabled !== false && (
                                        <div className="rounded-2xl border border-yellow-100 bg-yellow-50/70 p-4 dark:border-yellow-900/30 dark:bg-yellow-950/10">
                                            <div className="flex items-start justify-between gap-3">
                                                <div>
                                                    <p className="text-xs font-black uppercase tracking-[0.18em] text-yellow-700 dark:text-yellow-300">Öğlen Planı</p>
                                                    <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">Öğleni nasıl yöneteceğimizi buradan seçebilirsin</p>
                                                    <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                                                        Bu tercih Smart Search ve günlük öğlen akışını etkiler.
                                                    </p>
                                                </div>
                                                <span className="material-symbols-outlined text-[24px] text-yellow-600 dark:text-yellow-300">lunch_dining</span>
                                            </div>

                                            <div className="mt-4 rounded-2xl border border-slate-200 bg-white/80 p-4 dark:border-slate-700 dark:bg-slate-900/60">
                                                <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">Öğlen yemeğini kaç kişi için düzenleyeyim?</p>
                                                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                                                    {LUNCH_SERVING_OPTIONS.map((option) => {
                                                        const selected = config?.lunch?.preference?.selectedServingSize === option.value;
                                                        return (
                                                            <button
                                                                key={String(option.value)}
                                                                type="button"
                                                                onClick={() => setConfig({
                                                                    ...config,
                                                                    lunch: {
                                                                        ...config.lunch,
                                                                        preference: {
                                                                            ...(config.lunch?.preference || {}),
                                                                            selectedServingSize: option.value
                                                                        }
                                                                    }
                                                                })}
                                                                className={`rounded-2xl border px-3 py-3 text-left text-sm font-bold transition ${
                                                                    selected
                                                                        ? 'border-amber-500 bg-amber-500 text-white shadow-lg shadow-amber-500/20'
                                                                        : 'border-slate-200 bg-white text-slate-700 hover:border-amber-300 dark:border-slate-700 dark:bg-[#0d1326] dark:text-slate-200'
                                                                }`}
                                                            >
                                                                {option.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                                <p className="mt-3 text-xs font-medium text-slate-500 dark:text-slate-400">{lunchServingMeaningText}</p>
                                            </div>

                                            <div className="mt-4 grid gap-3">
                                                {LUNCH_MODE_OPTIONS.map((option) => {
                                                    const selected = config?.lunch?.preference?.mode === option.value;
                                                    return (
                                                        <button
                                                            key={option.value}
                                                            type="button"
                                                            onClick={() => setConfig({
                                                                ...config,
                                                                lunch: {
                                                                    ...config.lunch,
                                                                    preference: {
                                                                        ...(config.lunch?.preference || {}),
                                                                        mode: option.value
                                                                    }
                                                                }
                                                            })}
                                                            className={`rounded-[1.25rem] border px-4 py-4 text-left transition ${
                                                                selected
                                                                    ? 'border-amber-400 bg-white shadow-lg shadow-amber-500/10 dark:border-amber-300 dark:bg-slate-900/70'
                                                                    : 'border-slate-200 bg-white/70 hover:border-amber-200 dark:border-slate-700 dark:bg-[#0b0f1d]'
                                                            }`}
                                                        >
                                                            <div className="flex items-start justify-between gap-3">
                                                                <div>
                                                                    <p className="text-sm font-black text-slate-900 dark:text-white">{option.label}</p>
                                                                    <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">{option.description}</p>
                                                                </div>
                                                                {option.badge ? (
                                                                    <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-red-600 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
                                                                        <span className="material-symbols-outlined text-[14px]">warning</span>
                                                                        {option.badge}
                                                                    </span>
                                                                ) : null}
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            {config?.lunch?.preference?.mode === 'leftover_only' && (
                                                <p className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-medium text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-200">
                                                    Öğlen için ayrı menü açılmayacak. Saat geldiğinde yalnızca yumuşak bir hatırlatma gönderilecek.
                                                </p>
                                            )}

                                            <div className="mt-4">
                                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                                    Bu tercih ana `Değişiklikleri Kaydet` akışıyla birlikte saklanır.
                                                </p>
                                            </div>
                                        </div>
                                        )}
                                        <div className="space-y-4">
                                            {['SOUP', 'MAIN', 'SALAD', 'DESSERT'].map((type) => (
                                                <div key={type} className="flex items-center justify-between">
                                                    <span className="text-sm capitalize">{type === 'SOUP' ? 'Çorba' : type === 'MAIN' ? 'Ana Yemek' : type === 'SALAD' ? 'Salata' : 'Tatlı'}</span>
                                                    <input
                                                        checked={(config.lunch?.structure || []).includes(type)}
                                                        onChange={() => toggleStructure('lunch', type)}
                                                        className="rounded text-primary focus:ring-primary w-5 h-5 bg-slate-100 dark:bg-slate-900 border-none"
                                                        type="checkbox"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="space-y-6">
                                        <div className="flex items-center gap-3 pb-2 border-b border-slate-100 dark:border-slate-700">
                                            <span className="material-symbols-outlined text-blue-500">dark_mode</span>
                                            <div>
                                                <h3 className="text-lg font-semibold uppercase tracking-wide">Akşam</h3>
                                                <p className="text-[10px] text-slate-400">Akşam yemeği yapısını buradan belirleyebilirsiniz.</p>
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <p className="text-xs font-bold text-slate-500 uppercase px-1">Men? Tercihiniz</p>
                                            {['SOUP', 'MAIN', 'SALAD', 'DESSERT'].map((type) => (
                                                <div key={type} className="flex items-center justify-between p-2 hover:bg-slate-50 dark:hover:bg-slate-900/40 rounded-lg transition-colors">
                                                    <span className="text-sm capitalize">{type === 'SOUP' ? 'Çorba' : type === 'MAIN' ? 'Ana Yemek' : type === 'SALAD' ? 'Salata' : 'Tatlı'}</span>
                                                    <input
                                                        checked={(config.dinner?.structure || []).includes(type)}
                                                        onChange={() => toggleStructure('dinner', type)}
                                                        className="rounded text-primary focus:ring-primary w-5 h-5 bg-slate-100 dark:bg-slate-900 border-none"
                                                        type="checkbox"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Premium Kitchens (Athlete/Diet Integration) */}
                        {config.user.hasAthletePremium && (
                            <section className="bg-gradient-to-br from-slate-900 to-slate-800 p-8 rounded-2xl shadow-xl border border-green-500/20 relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform duration-500">
                                    <span className="material-symbols-outlined text-[120px] text-green-500">sports_score</span>
                                </div>

                                <div className="relative z-10">
                                    <div className="flex items-center gap-3 mb-8">
                                        <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center border border-green-500/30">
                                            <span className="material-symbols-outlined text-green-500">fitness_center</span>
                                        </div>
                                        <div>
                                            <h2 className="text-2xl font-black text-white font-display">Premium Mutfaklar</h2>
                                            <p className="text-xs text-slate-400 font-medium">Sporcu ve Diyet mutfağı yönetim merkezi.</p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Athlete Plan Card */}
                                        <div className="bg-slate-800/50 border border-slate-700/50 p-6 rounded-[2rem] hover:border-green-500/40 transition-all">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="material-symbols-outlined text-green-400">bolt</span>
                                                    <h3 className="text-white font-black text-sm uppercase tracking-widest">SPORCU PLANI</h3>
                                                </div>
                                                <span className="px-3 py-1 bg-green-500/10 text-green-500 text-[10px] font-black rounded-full border border-green-500/20">AKTİF</span>
                                            </div>

                                            <div className="space-y-4">
                                                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-2xl border border-slate-700/30">
                                                    <div className="flex items-center gap-3">
                                                        <span className="material-symbols-outlined text-slate-400">notifications</span>
                                                        <span className="text-xs text-slate-300 font-bold">Hatırlatıcı Saati</span>
                                                    </div>
                                                    <input
                                                        type="time"
                                                        value={config.user.intakeSurvey?.answers?.athleteReminderTime || '10:00'}
                                                        onChange={(e) => handleSurveyChange('athleteReminderTime', e.target.value)}
                                                        className="bg-transparent border-none text-white font-black text-lg p-0 focus:ring-0 w-20 text-right"
                                                    />
                                                </div>

                                                <button
                                                    onClick={() => navigate('/athlete-kitchen')}
                                                    className="w-full h-12 bg-white/5 hover:bg-white/10 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 border border-white/10"
                                                >
                                                    <span className="material-symbols-outlined text-sm">open_in_new</span>
                                                    Mutfağa Git
                                                </button>
                                            </div>
                                        </div>

                                        {/* Info Card */}
                                        <div className="bg-slate-800/50 border border-slate-700/50 p-6 rounded-[2rem] flex flex-col justify-center">
                                            <h4 className="text-slate-300 font-bold text-sm mb-2">Şefim, Hazır Mısınız?</h4>
                                            <p className="text-slate-500 text-[11px] leading-relaxed">
                                                Sporcu mutfağınızdaki planlama verileri buradaki ayarlarla senkronizedir.
                                                Hatırlatıcı saatiniz geldiğinde günün onaylı yemeklerini size bildireceğiz.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        )}

                        {/* Bildirim Ayarları */}
                        <section className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
                            <div className="flex items-center gap-2 mb-8">
                                <span className="material-symbols-outlined text-primary">notifications_active</span>
                                <h2 className="text-2xl font-bold font-display">Bildirim Ayarları</h2>
                            </div>
                            <div className="space-y-10">
                                {/* Kahvaltı */}
                                {!config.skipsBreakfast && (
                                    <div className="space-y-4">
                                        <div className="flex items-start justify-between gap-4">
                                            <div>
                                                <p className="font-semibold mb-1">Kahvaltı Bildirimi</p>
                                                <p className="text-xs text-slate-500">Her sabah günün ilk öğünü için hatırlatıcı al.</p>
                                            </div>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input
                                                    checked={config.notifications.send_weekend}
                                                    onChange={(e) => setConfig({ ...config, notifications: { ...config.notifications, send_weekend: e.target.checked } })}
                                                    className="sr-only peer"
                                                    type="checkbox"
                                                />
                                                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none dark:peer-focus:ring-red-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-primary"></div>
                                            </label>
                                        </div>
                                        {config.notifications.send_weekend && (
                                            <div className="p-3 bg-red-50 dark:bg-red-900/10 rounded-xl mb-2 flex items-center gap-2">
                                                <span className="material-symbols-outlined text-red-500 text-sm">event_repeat</span>
                                                <p className="text-[11px] text-red-700 dark:text-red-300">Hafta sonu bildirimleri aktif.</p>
                                            </div>
                                        )}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
                                                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Hafta içi</span>
                                                <input
                                                    value={config.notifications.breakfast_workday}
                                                    onChange={(e) => setConfig({ ...config, notifications: { ...config.notifications, breakfast_workday: e.target.value } })}
                                                    className="bg-transparent border-none text-sm font-semibold text-primary focus:ring-0 p-0 text-right w-20"
                                                    type="time"
                                                />
                                            </div>
                                            <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
                                                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Hafta sonu</span>
                                                <input
                                                    value={config.notifications.breakfast_weekend}
                                                    onChange={(e) => setConfig({ ...config, notifications: { ...config.notifications, breakfast_weekend: e.target.value } })}
                                                    className="bg-transparent border-none text-sm font-semibold text-primary focus:ring-0 p-0 text-right w-20"
                                                    type="time"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Kurtarma Ürün */}
                                <div className="space-y-4">
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <p className="font-semibold mb-1">Kurtarma Ürün İkazı</p>
                                            <p className="text-xs text-slate-500">Son kullanma tarihi yaklaşan ürünler için uyarı al.</p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input checked={true} className="sr-only peer" type="checkbox" readOnly />
                                            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none dark:peer-focus:ring-red-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-primary"></div>
                                        </label>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
                                            <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Hafta içi</span>
                                            <input
                                                value={config.notifications.rescue_workday}
                                                onChange={(e) => setConfig({ ...config, notifications: { ...config.notifications, rescue_workday: e.target.value } })}
                                                className="bg-transparent border-none text-sm font-semibold text-primary focus:ring-0 p-0 text-right w-20"
                                                type="time"
                                            />
                                        </div>
                                        <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
                                            <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Hafta sonu</span>
                                            <input
                                                value={config.notifications.rescue_weekend}
                                                onChange={(e) => setConfig({ ...config, notifications: { ...config.notifications, rescue_weekend: e.target.value } })}
                                                className="bg-transparent border-none text-sm font-semibold text-primary focus:ring-0 p-0 text-right w-20"
                                                type="time"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Otomatik Tarif */}
                                <div className="space-y-4">
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <p className="font-semibold mb-1">Otomatik Tarif Önerisi</p>
                                            <p className="text-xs text-slate-500">Eldeki malzemelere göre her gün otomatik tarif öner.</p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                checked={config.notifications.send_weekend_auto_menu !== false}
                                                onChange={(e) => setConfig({
                                                    ...config,
                                                    notifications: { ...config.notifications, send_weekend_auto_menu: e.target.checked }
                                                })}
                                                className="sr-only peer"
                                                type="checkbox"
                                            />
                                            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none dark:peer-focus:ring-red-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-primary"></div>
                                        </label>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
                                            <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Hafta içi Saat</span>
                                            <input
                                                value={config.notifications.auto_menu_workday}
                                                onChange={(e) => setConfig({
                                                    ...config,
                                                    hasSetAutoMenuTiming: true,
                                                    notifications: { ...config.notifications, auto_menu_workday: e.target.value }
                                                })}
                                                className="bg-transparent border-none text-sm font-semibold text-primary focus:ring-0 p-0 text-right w-20"
                                                type="time"
                                            />
                                        </div>
                                        <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
                                            <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Hafta sonu Saat</span>
                                            <input
                                                value={config.notifications.auto_menu_weekend}
                                                onChange={(e) => setConfig({
                                                    ...config,
                                                    hasSetAutoMenuTiming: true,
                                                    notifications: { ...config.notifications, auto_menu_weekend: e.target.value }
                                                })}
                                                className="bg-transparent border-none text-sm font-semibold text-primary focus:ring-0 p-0 text-right w-20"
                                                type="time"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Alışveriş Paylaşımı */}
                                <div className="space-y-4">
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <p className="font-semibold mb-1">Alışveriş Listesi Paylaşımı</p>
                                            <p className="text-xs text-slate-500">Liste güncellendiğinde yardımcı şef'e bildirim gönder.</p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                checked={config.auto_share}
                                                onChange={(e) => setConfig({ ...config, auto_share: e.target.checked })}
                                                className="sr-only peer"
                                                type="checkbox"
                                            />
                                            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none dark:peer-focus:ring-red-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-primary"></div>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-10 p-4 bg-red-50 dark:bg-red-900/10 rounded-xl border border-red-100 dark:border-red-900/30 flex gap-3">
                                <span className="material-symbols-outlined text-primary text-[20px]">info</span>
                                <p className="text-sm text-red-800 dark:text-red-200">Bildirimler tarayıcı ve uygulama bazlı olarak gönderilir. Tercihlerinizi istediğiniz zaman bu panelden güncelleyebilirsiniz.</p>
                            </div>
                        </section>
                    </div>
                </div>
            </div >



            <div className="sticky bottom-4 z-40 mt-8 flex justify-end">
                {weeklyKarne ? (
                    <button
                        onClick={() => setActivePlannerTab('report')}
                        className="bg-primary text-white px-6 py-3 rounded-full font-semibold shadow-lg shadow-red-500/20 flex items-center gap-2 hover:bg-red-700 transition-all"
                    >
                        <span className="material-symbols-outlined text-[20px]">insights</span>
                        Haftalık Başarı Grafiği
                    </button>
                ) : (
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className={`${saving ? 'opacity-70 cursor-not-allowed' : 'hover:bg-red-700'} bg-primary text-white px-6 py-3 rounded-full font-semibold transition-all shadow-lg shadow-red-500/20 flex items-center gap-2`}
                    >
                        <span className="material-symbols-outlined text-[20px]">{saving ? 'sync' : 'save'}</span>
                        {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                    </button>
                )}
            </div>

            {/* Family Member Modal */}
            < AnimatePresence >
                {showMemberModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
                        onClick={() => {
                            setShowMemberModal(false);
                            setEditingMember(null);
                        }}
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-lg w-full my-8 max-h-[90vh] flex flex-col overflow-hidden"
                        >
                            {/* Modal Header */}
                            <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 p-6 z-10">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-2xl font-bold font-display">
                                        {editingMember?.isSelf
                                            ? 'Alerjilerinizi Düzenleyin'
                                            : editingMember?.index !== undefined
                                                ? 'Aile Bireyini Düzenle'
                                                : 'Yeni Aile Bireyi Ekle'
                                        }
                                    </h3>
                                    <button
                                        onClick={() => {
                                            setShowMemberModal(false);
                                            setEditingMember(null);
                                        }}
                                        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                                    >
                                        <span className="material-symbols-outlined">close</span>
                                    </button>
                                </div>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6 space-y-6 overflow-y-auto flex-1 custom-scrollbar">
                                {/* Photo Upload */}
                                <div className="flex flex-col items-center gap-4 mb-4 mt-2">
                                    <div className="relative group">
                                        {editingMember?.photo ? (
                                            <img
                                                src={editingMember.photo}
                                                alt="Preview"
                                                className="w-24 h-24 rounded-full object-cover border-4 border-white dark:border-slate-800 shadow-xl"
                                            />
                                        ) : (
                                            <div className="w-24 h-24 rounded-full bg-slate-100 dark:bg-slate-900 flex items-center justify-center border-4 border-white dark:border-slate-800 shadow-xl text-slate-300">
                                                <span className="material-symbols-outlined text-4xl">
                                                    {editingMember?.role === 'partner' ? 'handshake' :
                                                        editingMember?.role === 'baby' ? 'baby_changing_station' :
                                                            'person'}
                                                </span>
                                            </div>
                                        )}
                                        <label className="absolute bottom-0 right-0 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-red-700 transition-colors shadow-lg border-2 border-white dark:border-slate-800">
                                            <span className="material-symbols-outlined text-sm">photo_camera</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={handlePhotoUpload}
                                            />
                                        </label>
                                    </div>
                                    <p className="text-xs text-slate-500">Profil fotoğrafını değiştirmek için tıkla</p>
                                </div>

                                {/* Cropper Interface Overlay */}
                                <AnimatePresence>
                                    {isCropping && imageToCrop && (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            className="absolute inset-0 z-50 bg-white dark:bg-slate-800 rounded-2xl flex flex-col"
                                        >
                                            <div className="flex-1 relative bg-black rounded-t-2xl overflow-hidden min-h-[300px]">
                                                <Cropper
                                                    image={imageToCrop}
                                                    crop={crop}
                                                    zoom={zoom}
                                                    aspect={1}
                                                    cropShape="round"
                                                    showGrid={true}
                                                    onCropChange={setCrop}
                                                    onCropComplete={onCropComplete}
                                                    onZoomChange={setZoom}
                                                />
                                            </div>
                                            <div className="p-6 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 rounded-b-2xl">
                                                <div className="flex flex-col gap-4">
                                                    <div className="flex items-center gap-4">
                                                        <span className="material-symbols-outlined text-slate-400">zoom_in</span>
                                                        <input
                                                            type="range"
                                                            value={zoom}
                                                            min={1}
                                                            max={3}
                                                            step={0.1}
                                                            aria-labelledby="Zoom"
                                                            onChange={(e) => setZoom(e.target.value)}
                                                            className="flex-1 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-primary"
                                                        />
                                                    </div>
                                                    <div className="flex gap-3">
                                                        <button
                                                            onClick={() => {
                                                                setIsCropping(false);
                                                                setImageToCrop(null);
                                                            }}
                                                            className="flex-1 px-4 py-3 border border-slate-200 dark:border-slate-700 rounded-xl font-semibold hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
                                                        >
                                                            İptal
                                                        </button>
                                                        <button
                                                            onClick={handleConfirmCrop}
                                                            className="flex-1 px-4 py-3 bg-primary text-white rounded-xl font-bold shadow-lg shadow-primary/20 hover:bg-primary/90 transition-all flex items-center justify-center gap-2"
                                                        >
                                                            <span className="material-symbols-outlined text-sm">check</span>
                                                            Kesmeyi Tamamla
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Name Input */}
                                {!editingMember?.isSelf && (
                                    <div>
                                        <label className="block text-sm font-semibold mb-2">
                                            İsim <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={editingMember?.name || ''}
                                            onChange={(e) => setEditingMember({ ...editingMember, name: e.target.value })}
                                            className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border-none rounded-xl focus:ring-2 focus:ring-primary/20"
                                            placeholder="Örn: Samira, Bera, vb."
                                        />
                                    </div>
                                )}

                                {/* Role Selection */}
                                {!editingMember?.isSelf && (
                                    <div>
                                        <label className="block text-sm font-semibold mb-3">
                                            Rol <span className="text-red-500">*</span>
                                        </label>
                                        <div className="grid grid-cols-4 gap-3">
                                            {[
                                                { value: 'partner', label: 'Partner/Eş', icon: 'handshake', color: 'indigo' },
                                                { value: 'child', label: 'Çocuk', icon: 'child_care', color: 'orange' },
                                                { value: 'baby', label: 'Bebek', icon: 'baby_changing_station', color: 'pink' },
                                                { value: 'other', label: 'Diğer', icon: 'person', color: 'slate' },
                                            ].map((role) => (
                                                <button
                                                    key={role.value}
                                                    onClick={() => setEditingMember({ ...editingMember, role: role.value })}
                                                    className={`p-4 rounded-xl border-2 transition-all ${editingMember?.role === role.value
                                                        ? `border-${role.color}-500 bg-${role.color}-50 dark:bg-${role.color}-900/20`
                                                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                                                        }`}
                                                >
                                                    <span className={`material-symbols-outlined text-3xl mb-2 ${editingMember?.role === role.value ? `text-${role.color}-500` : 'text-slate-400'
                                                        }`}>
                                                        {role.icon}
                                                    </span>
                                                    <p className="text-sm font-medium">{role.label}</p>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Birthdate Input */}
                                {!editingMember?.isSelf && (
                                    <div>
                                        <label className="block text-sm font-semibold mb-2">
                                            Doğum Tarihi (Opsiyonel)
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="date"
                                                value={editingMember?.birthDate || ''}
                                                onChange={(e) => setEditingMember({ ...editingMember, birthDate: e.target.value })}
                                                className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border-none rounded-xl focus:ring-2 focus:ring-primary/20"
                                            />
                                            {editingMember?.birthDate && (
                                                <p className="text-xs text-slate-500 mt-2">
                                                    Yaş: {calculateAge(editingMember.birthDate)} yıl
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Allergen Selection */}
                                <div>
                                    <label className="block text-sm font-semibold mb-3">
                                        Alerjiler
                                    </label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {ALLERGEN_OPTIONS.map((allergen) => {
                                            const currentAllergies = editingMember?.allergies || [];
                                            const hasNoneSelected = currentAllergies.includes('Yok');
                                            const isNoneOption = allergen === 'Yok';
                                            const isChecked = currentAllergies.includes(allergen);
                                            const isDisabled = hasNoneSelected && !isNoneOption;

                                            return (
                                                <label
                                                    key={allergen}
                                                    className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${isDisabled
                                                        ? 'bg-slate-100 dark:bg-slate-800/60 opacity-60 cursor-not-allowed'
                                                        : 'bg-slate-50 dark:bg-slate-900 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800'
                                                        }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        disabled={isDisabled}
                                                        onChange={(e) => {
                                                            const current = editingMember?.allergies || [];
                                                            const updated = (() => {
                                                                if (allergen === 'Yok') {
                                                                    return e.target.checked ? ['Yok'] : [];
                                                                }
                                                                const withoutNone = current.filter(a => a !== 'Yok');
                                                                if (e.target.checked) return [...withoutNone, allergen];
                                                                return withoutNone.filter(a => a !== allergen);
                                                            })();
                                                            setEditingMember({ ...editingMember, allergies: updated });
                                                        }}
                                                        className="w-5 h-5 rounded text-primary focus:ring-primary border-slate-300 dark:border-slate-600"
                                                    />
                                                    <span className="text-sm font-medium">{allergen}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="sticky bottom-0 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 p-6 flex gap-3">
                                <button
                                    onClick={() => {
                                        setShowMemberModal(false);
                                        setEditingMember(null);
                                    }}
                                    className="flex-1 px-6 py-3 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl font-semibold hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors border border-slate-200 dark:border-slate-700"
                                >
                                    İptal
                                </button>
                                <button
                                    onClick={handleSaveMember}
                                    disabled={saving}
                                    className="flex-1 px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {saving ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                            Kaydediliyor...
                                        </>
                                    ) : (
                                        <>
                                            <span className="material-symbols-outlined text-[20px]">check</span>
                                            Kaydet
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence >
        </DashboardLayout >
    );
};

export default KitchenPlanner;


